import { SxProps, Theme } from '@mui/material';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useCommandPaletteTrigger } from '@watershed/shared-frontend/components/CommandPalette/appCommandPalette';
import SearchIcon from '@watershed/icons/components/Search';
import SettingsCommandPalettePage from './SettingsCommandPalettePage';
import { Trans } from '@lingui/react/macro';
import { CODE_FONT_FAMILY } from '@watershed/style/styleUtils';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import { SEARCH_BAR_HEIGHT } from '../utils/constants';
const commandButtonStyles: SxProps<Theme> = (theme) => ({
  backgroundColor: theme.palette.white,
  borderLeft: `0.5px solid ${theme.palette.grey30}`,
  px: 2,
  height: SEARCH_BAR_HEIGHT,
  width: '100%',
  cursor: 'pointer',
  boxShadow: (theme) => `0px 1px 0px 0px ${theme.palette.grey100}05`,
  '&:hover': {
    backgroundColor: theme.palette.grey05,
    transition: 'background-color 0.2s ease-in',
  },
  [theme.breakpoints.down('md')]: {
    height: '32px',
    maxWidth: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderLeft: 'none',
    backgroundColor: 'transparent',
    width: 'fit-content',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.grey20,
      borderRadius: '6px',
      transition: 'background-color 0.2s ease-in',
    },
  },
});

/**
 * Button, styled as an input, for triggering the settings command palette.
 * Can be placed in headers or other UI locations.
 *
 */
export default function SettingsCommandButton() {
  const triggerCommandPalette = useCommandPaletteTrigger();

  const isSmallScreen = useMediaQuery('(max-width: 900px)');

  const handleClick = () => {
    triggerCommandPalette({
      component: SettingsCommandPalettePage,
      props: {},
    });
  };

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      onClick={handleClick}
      sx={commandButtonStyles}
      role="button"
      tabIndex={0}
      data-testid={TestIds.SettingsCommandButton}
    >
      <Stack flexDirection="row" alignItems="center" gap={1}>
        <SearchIcon size={14} color="grey50" />
        <Typography
          variant="body2"
          sx={{
            userSelect: 'none',
            display: isSmallScreen ? 'none' : 'block',
            color: (theme) => theme.palette.grey50,
          }}
        >
          <Trans context="Placeholder text for settings search">
            Search...
          </Trans>
        </Typography>
      </Stack>
      <Stack
        flexDirection="row"
        alignItems="center"
        sx={{ display: isSmallScreen ? 'none' : 'block' }}
      >
        <Box
          component="kbd"
          sx={{
            fontFamily: CODE_FONT_FAMILY,
            fontSize: 12,
            lineHeight: '16px',
            px: 0.5,
            border: (theme) => `0.5px solid ${theme.palette.grey30}`,
            borderRadius: 0.5,
          }}
        >
          <Trans context="Keyboard shortcut for settings search">⌘</Trans>
        </Box>
        <Box
          component="kbd"
          sx={{
            fontFamily: CODE_FONT_FAMILY,
            fontSize: 12,
            lineHeight: '16px',
            px: 0.5,
            ml: 0.5,
            border: (theme) => `0.5px solid ${theme.palette.grey30}`,
            borderRadius: 0.5,
          }}
        >
          <Trans context="Keyboard shortcut for settings search">S</Trans>
        </Box>
      </Stack>
    </Stack>
  );
}
